import { hangupLabel, hangupTone, type Cdr, type CdrSummary } from "@/lib/types";

/**
 * How a call leg ended, in the Calls page's words, now that a CDR carries its caller's queue stay
 * (ADR-025 in the platform repo). Pure, for `test/call-outcome.test.ts`.
 *
 * The switch answers an inbound caller before it parks them in a queue, so a caller who hung up
 * while waiting leaves a leg with an answer, billed seconds and NORMAL_CLEARING, the same as a
 * conversation. `queue_outcome` is how their stay ended, copied onto the leg by the event-tracker;
 * where it says the caller never talked to a rep, it is what the leg reads as.
 */

export type OutcomeTone = "neutral" | "warning" | "danger";

/** The queue outcomes that say the caller never had a conversation, as the page words them. */
export const QUEUE_OUTCOME_LABELS: Record<string, string> = {
  ABANDONED: "Hung up in queue",
  TIMEOUT: "No rep in time",
  DROP: "Dropped at pickup",
};

/**
 * A lead the dialer abandoned under safe harbour. Its queue stay also ends ABANDONED, but the lead
 * did not hang up: the dialer did, and `abandoned` is the dialer's own flag on the leg.
 */
export const DIALER_ABANDONED_LABEL = "Dropped by the dialer";

/** What the Abandoned tile says when it has nothing to break down. */
export const ABANDONED_DESCRIPTION =
  "Never reached a rep: hung up or timed out in a queue, or dropped by the dialer";

/** A caller who was blocked on the call (ADR-159 in platform), by how they were turned away. */
export const BLOCKED_LABELS: Record<"number" | "hidden", string> = {
  number: "Blocked",
  hidden: "Blocked, hidden caller",
};

/** The badge on a leg: a blocked caller, else the dialer's abandon, else a queue's wording, else
 *  the hangup cause's. A blocked call says so first: its hangup cause (busy, a normal clearing)
 *  reads like any other call's. */
export function callOutcome(
  call: Pick<Cdr, "hangup_cause" | "abandoned" | "queue_outcome" | "blocked">,
): { label: string; tone: OutcomeTone } {
  if (call.blocked) return { label: BLOCKED_LABELS[call.blocked], tone: "danger" };
  if (call.abandoned) return { label: DIALER_ABANDONED_LABEL, tone: "warning" };
  const queued = call.queue_outcome ? QUEUE_OUTCOME_LABELS[call.queue_outcome] : undefined;
  if (queued) return { label: queued, tone: "warning" };
  return { label: hangupLabel(call.hangup_cause), tone: hangupTone(call.hangup_cause) };
}

/** Which call of its cadence a dialer call was, as the Calls page and a contact's timeline mark
 *  it beside the outcome. */
export interface CallMark {
  /** "Call 1", "Redial 1 of 2". */
  label: string;
  /** A redial is drawn in the accent tone, with its icon; the call itself is plain. */
  redial: boolean;
  /** The mark in a sentence, for the tooltip. */
  title: string;
}

/**
 * The mark on a dialer call (ADR-105 in `platform`): "Call 1" for the first call of its cadence
 * round, "Redial 1 of 2" for the first of the two redials that call carried. A redial is a call
 * of its own on every list, made at once after an attempt nobody answered, so without the mark
 * three rows seconds apart read as a fault.
 *
 * Null where the dialer placed no cadence call: an inbound call, a number dialed by hand, a call
 * from before redials. A redial from before its call's count was recorded reads "Redial 1".
 */
export function callMark(
  call: Pick<Cdr, "step_n" | "redial_n" | "redials_of">,
): CallMark | null {
  const n = call.redial_n;
  if (n === null || n === undefined) return null;
  const step = call.step_n ?? null;
  const of = call.redials_of ?? null;
  if (n === 0) {
    if (step === null) return null;
    const redials = of ? `, with ${of} ${of === 1 ? "redial" : "redials"} if nobody answers` : "";
    return { label: `Call ${step}`, redial: false, title: `Call ${step} of its round on the cadence${redials}` };
  }
  const label = of === null ? `Redial ${n}` : `Redial ${n} of ${Math.max(of, n)}`;
  const after = step === null ? "its call" : `call ${step}`;
  return { label, redial: true, title: `${label} of ${after}: made at once, when nobody answered` };
}

/**
 * Whether the Talk column shows the leg's billed seconds. Not for a leg nobody answered, and not for
 * a caller who hung up in a queue or whom no rep took in time: the switch billed them for holding.
 * A drop at pickup did reach a rep, so it keeps its second or two.
 */
export function talkShown(call: Pick<Cdr, "answer_time" | "queue_outcome">): boolean {
  if (call.answer_time === null) return false;
  return call.queue_outcome !== "ABANDONED" && call.queue_outcome !== "TIMEOUT";
}

/**
 * The Abandoned tile: every leg that never reached a rep, and who ended it. The platform counts a
 * lead the dialer abandoned in `abandoned` only, although its stay says ABANDONED too, so the three
 * counts add up without counting a leg twice. A platform older than the queue counts sends none.
 */
export function abandonedTile(summary: CdrSummary): { value: number; hint: string } {
  const inQueue = (summary.queue_abandoned ?? 0) + (summary.queue_timed_out ?? 0);
  const byDialer = summary.abandoned;
  const parts: string[] = [];
  if (inQueue > 0) parts.push(`${inQueue.toLocaleString()} in a queue`);
  if (byDialer > 0) parts.push(`${byDialer.toLocaleString()} by the dialer`);
  return {
    value: inQueue + byDialer,
    hint: parts.length > 0 ? parts.join(" · ") : ABANDONED_DESCRIPTION,
  };
}

/** The queue outcomes the summary counts, and so the ones a chip can offer. */
export type QueueChipOutcome = "ABANDONED" | "TIMEOUT";

export interface QueueOutcomeChip {
  outcome: QueueChipOutcome;
  label: string;
  count: number;
}

/**
 * The queue-outcome chips worth showing: those with legs behind them, plus the selected one even at
 * zero, so a chip another filter emptied can still be switched off.
 */
export function queueOutcomeChips(
  summary: CdrSummary | undefined,
  selected: QueueChipOutcome | null = null,
): QueueOutcomeChip[] {
  if (!summary) return [];
  const chips: QueueOutcomeChip[] = [
    { outcome: "ABANDONED", label: "Hung up in queue", count: summary.queue_abandoned ?? 0 },
    { outcome: "TIMEOUT", label: "No rep in time", count: summary.queue_timed_out ?? 0 },
  ];
  return chips.filter((chip) => chip.count > 0 || chip.outcome === selected);
}

/**
 * The filters a queue-outcome chip narrows the report to: exactly the legs its count covers. A lead
 * the dialer abandoned has the ABANDONED outcome too but is not in `queue_abandoned`, so the chip
 * leaves it out as well, and the rows under the chip always number what it said.
 */
export function queueOutcomeFilter(
  outcome: QueueChipOutcome | null,
): { queue_outcome?: string; abandoned?: boolean } {
  if (outcome === null) return {};
  return outcome === "ABANDONED" ? { queue_outcome: outcome, abandoned: false } : { queue_outcome: outcome };
}

/* ------------------------------------------------------------------------------------------ */
/* Who put the phone down                                                                      */
/* ------------------------------------------------------------------------------------------ */

/**
 * Who this leg's far end is, in the words a reader of the report would use.
 *
 * A CDR is one channel: the switch at one end and somebody's phone at the other. Which somebody
 * is what `leg` says — a rep's browser on a `rep` leg, the outside party on every other. `other`
 * is a leg nothing stamped, so it falls back to the direction: a channel that arrived at the
 * switch was placed by whoever is on it, and one the switch created was placed to them.
 */
export function farParty(call: Pick<Cdr, "leg" | "direction">): string {
  if (call.leg === "rep") return "The rep";
  if (call.leg === "lead") return "The lead";
  if (call.leg === "inbound" || call.leg === "room") return "The caller";
  return call.direction === "inbound" ? "The caller" : "The other party";
}

/**
 * Who ended the call, from the disposition the endpoint module left on this leg.
 *
 * The rule is `recv_` against `send_`, and it is about THIS leg, not about the call:
 *
 * - `recv_*` — the far end of this leg acted. Their phone sent the BYE, cancelled before it was
 *   answered, or refused it. That party ended it.
 * - `send_*` — the switch ended this leg. On a two-leg call that is what happens to the second
 *   leg when the first one hangs up, so it does not name a person; it is honest about that and
 *   points at the other leg instead, which is a click away in the same dialog.
 *
 * Verified in the FreeSWITCH source (`mod_sofia.c`, `sofia.c`, `mod_verto.c`) and against
 * production pairs, where the two legs of a finished call always read opposite ways.
 */
export function endedBy(
  call: Pick<Cdr, "leg" | "direction"> & { hangup_disposition: string | null },
): { label: string; hint?: string } | null {
  const who = farParty(call);
  switch (call.hangup_disposition) {
    case "recv_bye":
      return { label: `${who} hung up` };
    case "send_bye":
      return {
        label: "Ended from our side",
        hint: "The switch put this leg down — on a two-party call, because the other one hung up",
      };
    case "recv_cancel":
      return { label: `${who} hung up before it was answered` };
    case "send_cancel":
      return {
        label: "Cancelled before it was answered",
        hint: "The switch stopped ringing this leg: the caller gave up, the line timed out, or another rep took it",
      };
    case "recv_refuse":
      return { label: `${who} rejected the call` };
    case "send_refuse":
      return {
        label: "Rejected from our side",
        hint: "The switch refused the call rather than ringing it",
      };
    default:
      return null;
  }
}

/** What a related leg was found by, as the dialog labels it. */
export const LEG_RELATION_LABELS: Record<string, string> = {
  room: "same room",
  bridge: "bridged",
  recording: "same recording",
};
